import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import InfoIcon from 'app/components/Icons/InfoIcon';
import { themePalette } from 'app/components/ThemeProvider/themeColors';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';

import { httpClient } from '../../../../api/http/http-client';
import RestrictionInfoModal from '../restriction-info';
import CardsTable from './cards-table';
import FixedBottomAction from './components/fixed-bottom-action';
import {
  mapProlongEmployee,
  SalaryCardsProlongParams,
  SalaryCardsProlongResponse,
  SalaryCardsProlongSubmitRequest,
  SalaryCardsProlongSubmitResponse,
} from './model/types';

const SEARCH_DEBOUNCE_MS = 400;

const CardExtensionPage = () => {
  const { t } = useTranslation('salary-card');
  const navigate = useCustomNavigate();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [openRestrictionInfo, setOpenRestrictionInfo] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryParams: SalaryCardsProlongParams = useMemo(
    () => ({
      search: searchText || undefined,
    }),
    [searchText]
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['salary-cards-prolong', queryParams],
    queryFn: async ({ signal }): Promise<SalaryCardsProlongResponse> =>
      await httpClient.get('/api-web/v1/main/salary_cards_prolong', {
        params: queryParams,
        signal,
      }),
    keepPreviousData: true,
    onSuccess: response => {
      if (response.subscribe === 'N') {
        setOpenRestrictionInfo(true);
      }
    },
  });

  const { mutate: submitProlong, isLoading: isSubmitting } = useMutation({
    retry: 1,
    mutationKey: ['salary-cards-prolong-submit'],
    mutationFn: async ({ business_key, prolong_employees_id }: SalaryCardsProlongSubmitRequest) =>
      await httpClient.post<SalaryCardsProlongSubmitResponse>(
        `/api-web/v1/main/salary_cards_prolong?business_key=${business_key}`,
        { prolong_employees_id }
      ),
  });

  const cards = useMemo(
    () => data?.prolong_employee?.map(mapProlongEmployee) ?? [],
    [data?.prolong_employee]
  );

  const selectedCount = selectedIds.size;
  const prolongEmployeesId = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const hasNoEmployees = !searchText && !isLoading && cards.length === 0;
  const isSearchPending = searchInput.trim() !== searchText;

  const handleContinue = () => {
    if (!data?.business_key) return;

    submitProlong(
      { business_key: data.business_key, prolong_employees_id: prolongEmployeesId },
      {
        onSuccess: response => {
          navigate('salary-card/extension/phone-numbers', {
            state: {
              business_key: response.business_key,
              selected_employee: response.selected_employee,
            },
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box pb={hasNoEmployees ? 0 : 20}>
      {hasNoEmployees ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 1,
            mt: '54px',
          }}
        >
          <InfoIcon sx={{ width: '24px', height: '24px', flexShrink: 0 }} />
          <Typography
            fontSize={20}
            fontWeight={600}
            lineHeight='28px'
            color={themePalette.text_and_icon.primary}
          >
            {t('cardExtension.emptyEmployees')}
          </Typography>
        </Box>
      ) : (
        <>
          <CardsTable
            cards={cards}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            search={searchInput}
            onSearchChange={setSearchInput}
            isFetching={isFetching || isSearchPending}
          />

          <FixedBottomAction>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 2,
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  lineHeight='20px'
                  color={themePalette.text_and_icon.secondary}
                >
                  {t('cardExtension.employeeCount')}
                </Typography>
                <Typography fontSize={14} fontWeight={600} lineHeight='20px'>
                  {selectedCount}
                </Typography>
              </Box>
              <Button
                disabled={selectedCount === 0 || isSubmitting}
                size='large'
                onClick={handleContinue}
                sx={{ width: 200, height: 56 }}
              >
                {t('cardExtension.continue')}
              </Button>
            </Box>
          </FixedBottomAction>
        </>
      )}
      <RestrictionInfoModal open={openRestrictionInfo} setOpen={setOpenRestrictionInfo} />
    </Box>
  );
};

export default CardExtensionPage;
